import * as React from 'react';
import * as Yup from 'yup';
import { Box, Divider, IconButton, Tooltip, Typography } from '@mui/material';
import { getIn, useField, useFormikContext } from 'formik';
import { TZDate } from '@date-fns/tz';
import { format, isAfter, isBefore } from 'date-fns';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

import { SwitchFormField } from 'lib/form/fields/SwitchFormField';
import { TextFormField } from 'lib/form/fields/TextFormField';
import { DateTimePickerFormField } from 'lib/form/fields/DateTimePickerFormField';
import { Row } from 'ui/Flex';
import { weekdays } from 'components/perks/award-enrichments/consts';
import { IItemContext, IModuleSchedule } from '../../models/ContentModule';

export interface DaySchedule {
    active: boolean;
    timeFrom: string;
    timeTo: string;
}

export interface ModuleSchedulingFormValues {
    days: DaySchedule[];
    hasSpecificDates: boolean;
    startDateTime: Date | null;
    endDateTime: Date | null;
}

const DEFAULT_FROM = '09:00';
const DEFAULT_TO = '17:00';

const timeStringToSeconds = (time: string): number => {
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 3600 + minutes * 60;
};

const secondsToTimeString = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

export const buildSchedule = (days: DaySchedule[]): IModuleSchedule[] =>
    days.reduce<IModuleSchedule[]>((acc, day, index) => {
        if (!day.active || !day.timeFrom || !day.timeTo) {
            return acc;
        }
        acc.push({
            daysOfWeek: Number(weekdays[index].value),
            timeFrom: timeStringToSeconds(day.timeFrom),
            timeTo: timeStringToSeconds(day.timeTo)
        });
        return acc;
    }, []);

export function toMerchantISO(localInput: Date, merchantTimezone: string): string {
    // TZDate parses the wall-clock string as a time in the given zone
    const zonedDate = new TZDate(localInput, merchantTimezone);

    // The XXX token outputs offset as +01:00 / -05:00 / Z
    return format(zonedDate, "yyyy-MM-dd'T'HH:mm:ssXXX");
}

export const defaultModuleSchedulingFormValues: ModuleSchedulingFormValues = {
    days: weekdays.map((_, index) => {
        const isWeekday = index <= 4;
        return {
            active: isWeekday,
            timeFrom: isWeekday ? DEFAULT_FROM : '',
            timeTo: isWeekday ? DEFAULT_TO : ''
        };
    }),
    hasSpecificDates: false,
    startDateTime: null,
    endDateTime: null
};

export const moduleSchedulingValidationSchema = Yup.object().shape({
    days: Yup.array().of(
        Yup.object().shape({
            active: Yup.boolean(),
            timeFrom: Yup.string().when('active', {
                is: true,
                then: schema =>
                    schema
                        .required('Required')
                        .test('before-time-to', 'Start time must be before end time', function (value) {
                            const { timeTo } = this.parent as DaySchedule;
                            if (!value || !timeTo) {
                                return true;
                            }
                            return timeStringToSeconds(value) < timeStringToSeconds(timeTo);
                        })
            }),
            timeTo: Yup.string().when('active', {
                is: true,
                then: schema =>
                    schema
                        .required('Required')
                        .test('after-time-from', 'End time must be after start time', function (value) {
                            const { timeFrom } = this.parent as DaySchedule;
                            if (!value || !timeFrom) {
                                return true;
                            }
                            return timeStringToSeconds(value) > timeStringToSeconds(timeFrom);
                        })
            })
        })
    ),
    hasSpecificDates: Yup.boolean(),
    startDateTime: Yup.date()
        .nullable()
        .when('hasSpecificDates', {
            is: true,
            then: schema =>
                schema
                    .required('Required')
                    .test(
                        'before-end-date',
                        'Start date & time must be before end date & time',
                        function (value) {
                            const { endDateTime } = this.parent as ModuleSchedulingFormValues;
                            if (!value || !endDateTime) {
                                return true;
                            }
                            return !isAfter(value, endDateTime);
                        }
                    )
        }),
    endDateTime: Yup.date()
        .nullable()
        .when('hasSpecificDates', {
            is: true,
            then: schema =>
                schema
                    .required('Required')
                    .test(
                        'after-start-date',
                        'End date & time must be after start date & time',
                        function (value) {
                            const { startDateTime } = this.parent as ModuleSchedulingFormValues;
                            if (!value || !startDateTime) {
                                return true;
                            }
                            return !isBefore(value, startDateTime);
                        }
                    )
        })
});

export const buildModuleSchedulingFormValues = (
    existingSchedule: Pick<IItemContext, 'visibleTo' | 'visibleFrom' | 'schedule'>
): ModuleSchedulingFormValues | null => {
    const hasSchedule = !!existingSchedule.schedule?.length;
    const hasDates = !!(existingSchedule.visibleFrom || existingSchedule.visibleTo);
    if (!hasSchedule && !hasDates) {
        return null;
    }
    const scheduleByDay = new Map<number, IModuleSchedule>();
    existingSchedule.schedule?.forEach(entry => {
        if (entry.daysOfWeek !== undefined) {
            scheduleByDay.set(entry.daysOfWeek, entry);
        }
    });
    return {
        days: weekdays.map(({ value }) => {
            const match = scheduleByDay.get(Number(value));
            return match
                ? {
                      active: true,
                      timeFrom: secondsToTimeString(match.timeFrom),
                      timeTo: secondsToTimeString(match.timeTo)
                  }
                : { active: false, timeFrom: '', timeTo: '' };
        }),
        hasSpecificDates: hasDates,
        startDateTime: existingSchedule.visibleFrom ? new Date(existingSchedule.visibleFrom) : null,
        endDateTime: existingSchedule.visibleTo ? new Date(existingSchedule.visibleTo) : null
    };
};

const DAY_ROW_STYLE: React.CSSProperties = { minHeight: 56 };

interface FieldErrorMessageProps {
    names: string[];
}

const FieldErrorMessage = ({ names }: FieldErrorMessageProps) => {
    const { errors } = useFormikContext<ModuleSchedulingFormValues>();
    const error = names.map(name => getIn(errors, name)).find(Boolean);
    if (!error) {
        return null;
    }
    return (
        <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
            {error}
        </Typography>
    );
};

interface DayRowProps {
    dayOfWeek: number;
    label: React.ReactNode;
    onCopy: (sourceIndex: number) => void;
}

const DayRow = ({ dayOfWeek, label, onCopy }: DayRowProps) => {
    const activeName = `days[${dayOfWeek}].active`;
    const timeFromName = `days[${dayOfWeek}].timeFrom`;
    const timeToName = `days[${dayOfWeek}].timeTo`;
    const [activeField] = useField<boolean>(activeName);
    const active = activeField.value;

    const handleCopy = React.useCallback(() => {
        onCopy(dayOfWeek);
    }, [dayOfWeek, onCopy]);

    return (
        <Row valign="center" gutter style={DAY_ROW_STYLE}>
            <Box width={140}>
                <SwitchFormField name={activeName} label={label} />
            </Box>
            {active && (
                <Box sx={{ flex: 1, width: '100%' }}>
                    <Row valign="center" gutter>
                        <Box flex={1}>
                            <TextFormField
                                name={timeFromName}
                                type="time"
                                size="small"
                                shrink
                                showHelperText={false}
                            />
                        </Box>
                        <Typography>-</Typography>
                        <Box flex={1}>
                            <TextFormField
                                name={timeToName}
                                type="time"
                                size="small"
                                shrink
                                showHelperText={false}
                            />
                        </Box>
                        <Tooltip title="Copy times to other active days">
                            <IconButton size="small" onClick={handleCopy}>
                                <ContentCopyIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Row>
                    <FieldErrorMessage names={[timeFromName, timeToName]} />
                </Box>
            )}
        </Row>
    );
};

export const ModuleSchedulingForm = ({ showHeader = true }: { showHeader?: boolean }) => {
    const { values, setFieldValue } = useFormikContext<ModuleSchedulingFormValues>();

    const handleCopy = React.useCallback(
        (sourceIndex: number) => {
            const source = values.days[sourceIndex];
            if (!source) {
                return;
            }
            values.days.forEach((day, index) => {
                if (index === sourceIndex || !day.active) {
                    return;
                }
                setFieldValue(`days[${index}].timeFrom`, source.timeFrom);
                setFieldValue(`days[${index}].timeTo`, source.timeTo);
            });
        },
        [setFieldValue, values.days]
    );

    return (
        <Box display="flex" flexDirection="column" gap={2}>
            {showHeader && (
                <>
                    <Typography variant="h6">Date / Time Restriction</Typography>
                    <Divider />
                    <Typography variant="subtitle2">Recurring Days</Typography>
                </>
            )}
            {!showHeader && <Divider />}
            <Box display="flex" flexDirection="column" gap={1} pt={1}>
                {weekdays.map((day, index) => (
                    <DayRow key={day.value} dayOfWeek={index} label={day.label} onCopy={handleCopy} />
                ))}
            </Box>
            <Divider />
            <SwitchFormField name="hasSpecificDates" label="Add specific dates" />
            {values.hasSpecificDates && (
                <Box>
                    <Row gutter>
                        <Box flex={1}>
                            <DateTimePickerFormField
                                name="startDateTime"
                                label="Start date & time"
                                variant="outlined"
                                showHelperText={false}
                            />
                        </Box>
                        <Box flex={1}>
                            <DateTimePickerFormField
                                name="endDateTime"
                                label="End date & time"
                                variant="outlined"
                                showHelperText={false}
                            />
                        </Box>
                    </Row>
                    <FieldErrorMessage names={['startDateTime', 'endDateTime']} />
                </Box>
            )}
        </Box>
    );
};
